import { createHash } from 'crypto'
import { and, eq, inArray } from 'drizzle-orm'
import { db as defaultDb } from '@/db'
import { knowledgeEntries, knowledgeEntryTranslations } from '@/db/schema'
import type { KnowledgeEntryKind } from '@/db/schema'
import type { SupportedLanguage } from '@/lib/user-language'
import type { PostgresJsDatabase } from 'drizzle-orm/postgres-js'
import type * as schema from '@/db/schema'

type DB = PostgresJsDatabase<typeof schema>
type KbViewerOptions = {
  includePending?: boolean
}

export function hashContent(content: string): string {
  return createHash('sha256').update(content).digest('hex').slice(0, 16)
}

export function isTranslationStale(sourceBodyHash: string, currentBody: string): boolean {
  return hashContent(currentBody) !== sourceBodyHash
}

export type KbEntryWithTranslation = {
  id: string
  slug: string
  title: string
  kind: KnowledgeEntryKind
  tags: string[]
  bodyMarkdown: string
  language: SupportedLanguage
  isTranslated: boolean
  translationStatus: 'approved' | 'pending' | 'stale' | null
}

/**
 * Returns a KB entry with a translation applied if one exists for the viewer's
 * language. Pending translations are only shown when expert review surfaces
 * explicitly opt in. Marks the result as stale if the translation no longer
 * matches the current source body.
 */
export async function getKbEntryForViewer(
  entryId: string,
  tenantId: string,
  viewerLanguage: SupportedLanguage,
  options: KbViewerOptions = {},
  db: DB = defaultDb as DB,
): Promise<KbEntryWithTranslation | null> {
  const [entry] = await db
    .select()
    .from(knowledgeEntries)
    .where(and(eq(knowledgeEntries.id, entryId), eq(knowledgeEntries.tenantId, tenantId)))
    .limit(1)

  if (!entry) return null

  if (viewerLanguage === entry.language) {
    return {
      id: entry.id,
      slug: entry.slug,
      title: entry.title,
      kind: entry.kind,
      tags: entry.tags,
      bodyMarkdown: entry.bodyMarkdown,
      language: entry.language as SupportedLanguage,
      isTranslated: false,
      translationStatus: null,
    }
  }

  const [translation] = await db
    .select()
    .from(knowledgeEntryTranslations)
    .where(
      and(
        eq(knowledgeEntryTranslations.entryId, entryId),
        eq(knowledgeEntryTranslations.language, viewerLanguage),
      ),
    )
    .limit(1)

  const canUseTranslation =
    translation?.reviewStatus === 'approved'
    || (options.includePending === true && translation?.reviewStatus === 'pending')

  if (!translation || !canUseTranslation) {
    return {
      id: entry.id,
      slug: entry.slug,
      title: entry.title,
      kind: entry.kind,
      tags: entry.tags,
      bodyMarkdown: entry.bodyMarkdown,
      language: entry.language as SupportedLanguage,
      isTranslated: false,
      translationStatus: translation ? (translation.reviewStatus as 'pending') : null,
    }
  }

  const stale = isTranslationStale(translation.sourceBodyHash, entry.bodyMarkdown)

  return {
    id: entry.id,
    slug: entry.slug,
    title: translation.title,
    kind: entry.kind,
    tags: entry.tags,
    bodyMarkdown: translation.bodyMarkdown,
    language: viewerLanguage,
    isTranslated: true,
    translationStatus: stale ? 'stale' : translation.reviewStatus === 'pending' ? 'pending' : 'approved',
  }
}

/**
 * Returns KB titles resolved to the viewer's language where a usable translation
 * exists. Pending translations are only shown when expert review surfaces opt in.
 */
export async function getKbEntryTitlesForViewer(
  tenantId: string,
  viewerLanguage: SupportedLanguage,
  entryIds: string[],
  options: KbViewerOptions = {},
  db: DB = defaultDb as DB,
): Promise<Map<string, { title: string; isTranslated: boolean; isStale: boolean }>> {
  const result = new Map<string, { title: string; isTranslated: boolean; isStale: boolean }>()

  if (viewerLanguage === 'en' || entryIds.length === 0) return result

  const translations = await db
    .select({
      entryId: knowledgeEntryTranslations.entryId,
      title: knowledgeEntryTranslations.title,
      reviewStatus: knowledgeEntryTranslations.reviewStatus,
      sourceBodyHash: knowledgeEntryTranslations.sourceBodyHash,
    })
    .from(knowledgeEntryTranslations)
    .where(
      and(
        eq(knowledgeEntryTranslations.tenantId, tenantId),
        eq(knowledgeEntryTranslations.language, viewerLanguage),
        inArray(knowledgeEntryTranslations.entryId, entryIds),
      ),
    )

  const approvedTranslations = translations.filter(
    (t) => t.reviewStatus === 'approved' || (options.includePending === true && t.reviewStatus === 'pending'),
  )

  for (const t of approvedTranslations) {
    result.set(t.entryId, { title: t.title, isTranslated: true, isStale: false })
  }

  return result
}

export type TranslationRecord = typeof knowledgeEntryTranslations.$inferSelect

/**
 * Fetches the raw translation record for a KB entry in a given language.
 * Returns null if no translation exists.
 */
export async function getKbTranslationRecord(
  entryId: string,
  language: SupportedLanguage,
  db: DB = defaultDb as DB,
): Promise<TranslationRecord | null> {
  const [translation] = await db
    .select()
    .from(knowledgeEntryTranslations)
    .where(
      and(
        eq(knowledgeEntryTranslations.entryId, entryId),
        eq(knowledgeEntryTranslations.language, language),
      ),
    )
    .limit(1)
  return translation ?? null
}

/**
 * Upserts a translation record for a KB entry. If a pending/rejected translation
 * already exists it is replaced; approved translations are not overwritten.
 */
export async function upsertKbTranslation(
  input: {
    tenantId: string
    entryId: string
    language: SupportedLanguage
    title: string
    bodyMarkdown: string
    flatMarkdown: string
    sourceRevisionNumber: number
    sourceBodyHash: string
    translationSource: 'llm' | 'human'
    createdById?: string
  },
  db: DB = defaultDb as DB,
): Promise<TranslationRecord> {
  const existing = await db
    .select()
    .from(knowledgeEntryTranslations)
    .where(
      and(
        eq(knowledgeEntryTranslations.entryId, input.entryId),
        eq(knowledgeEntryTranslations.language, input.language),
      ),
    )
    .limit(1)
    .then((rows) => rows[0] ?? null)

  if (existing?.reviewStatus === 'approved') {
    return existing
  }

  const values = {
    tenantId: input.tenantId,
    entryId: input.entryId,
    language: input.language,
    title: input.title,
    bodyMarkdown: input.bodyMarkdown,
    flatMarkdown: input.flatMarkdown,
    sourceRevisionNumber: input.sourceRevisionNumber,
    sourceBodyHash: input.sourceBodyHash,
    translationSource: input.translationSource,
    reviewStatus: input.translationSource === 'human' ? ('approved' as const) : ('pending' as const),
    createdById: input.createdById ?? null,
    updatedAt: new Date(),
  }

  if (existing) {
    const [updated] = await db
      .update(knowledgeEntryTranslations)
      .set(values)
      .where(eq(knowledgeEntryTranslations.id, existing.id))
      .returning()
    return updated
  }

  const [inserted] = await db
    .insert(knowledgeEntryTranslations)
    .values(values)
    .returning()
  return inserted
}
